import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Facility } from './Facility'
import { POSTRelatedResponseType } from '../infrastructure/HTTPPropertyRepository/schemas/related'
import { FacilityCodeGroup } from './FacilityCodeGroup'
import { FacilityID } from './FacilityID'
import { SearchProperty } from '../infrastructure/HTTPPropertyRepository/schemas/v3/search/legacy'

type NonEmptyArray<T> = [T, ...T[]]

export const ListOfFacilityValidation = z.object({
  list: z.instanceof(Facility, { message: '[ListOfFacility] list is required' }).array(),
})

export class ListOfFacility extends Model {
  static create({ list }: z.infer<typeof ListOfFacilityValidation>) {
    ListOfFacilityValidation.parse({ list })
    return new ListOfFacility(list)
  }

  static fromRelatedApi({
    json,
  }: {
    json: POSTRelatedResponseType['properties'][number]['facilities']
  }) {
    return ListOfFacility.create({
      list: json.map(facilityJSON =>
        Facility.create({
          codeGroup: FacilityCodeGroup.create({ value: facilityJSON.code_group }),
          id: FacilityID.create({ value: facilityJSON.id }),
        })
      ),
    })
  }

  static fromJSON({ json }: { json: ReturnType<ListOfFacility['toJSON']> }) {
    return ListOfFacility.create({
      list: json.list.map(facilityJSON =>
        Facility.create({
          codeGroup: FacilityCodeGroup.create({ value: facilityJSON.codeGroup }),
          id: FacilityID.create({ value: facilityJSON.id }),
        })
      ),
    })
  }

  static fromJSONOrEmpty({ json }: { json?: ReturnType<ListOfFacility['toJSON']> }) {
    try {
      if (!json) {
        return ListOfFacility.empty()
      }
      return ListOfFacility.fromJSON({ json })
    } catch {
      return ListOfFacility.empty()
    }
  }

  static fromSerpApiLegacyOrEmpty({ json }: { json: SearchProperty['facilities'] }) {
    try {
      if (!json) {
        return ListOfFacility.empty()
      }

      const facilities = Array.from(json)
        .filter(facilityJson => facilityJson && facilityJson.name)
        .map(facilityJson => {
          return Facility.fromSerpApiLegacy({ id: facilityJson.name })
        })

      return ListOfFacility.create({
        list: facilities,
      })
    } catch (error) {
      return ListOfFacility.empty()
    }
  }

  static empty() {
    return new ListOfFacility([])
  }

  constructor(private readonly _list: Facility[]) {
    super()
  }

  get list() { return this._list } // prettier-ignore

  toJSON() {
    return { list: this._list.map(item => item.toJSON()) }
  }

  map<T>(fn: (facility: Facility) => T) {
    return this._list.map(fn)
  }

  filter(fn: (facility: Facility) => boolean) {
    return new ListOfFacility(this._list.filter(fn))
  }

  take(end: number) {
    return new ListOfFacility(this._list.slice(0, end))
  }

  uniqueById() {
    return new ListOfFacility(
      this._list.filter(
        (facility, index, arr) => arr.findIndex(f => f.id === facility.id) === index
      )
    )
  }

  hasFacilities(): this is this & { list: NonEmptyArray<Facility> } {
    return this._list.length !== 0
  }

  isEmpty(): this is this & { list: [] } {
    return this._list.length === 0
  }
}
